<template>
  <div class="py-14">
    <DefaultGrid class="space-y-10">
      <div class="col-span-full flex flex-col space-y-4">
        <BaseProgressBar class="text-mint" :progress="progress" />
        <span class="type-body-sm">
          {{
            showSuccessPage
              ? 'Complete!'
              : `Step ${currentStep} of ${totalSteps}`
          }}
        </span>
      </div>
      <div class="col-span-full">
        <form class="space-y-10" @submit.prevent="onSubmit">
          <div v-if="currentStep === 1" class="space-y-7">
            <div
              v-for="(parent, index) in parentFields"
              :key="parent.key"
              class="space-y-7"
            >
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <h3 class="type-heading-5 font-medium">
                    {{
                      index === 0
                        ? 'Your Info (Parent / Guardian)'
                        : 'Additional Parent/Guardian info'
                    }}
                  </h3>
                  <RemoveButton v-if="index > 0" @click="removeParent(1)" />
                </div>
                <p class="text-[13px]/[20px]">
                  {{ index === 0 ? 'Your' : 'Their' }} info will never be shared
                  publicly.
                </p>
              </div>
              <div class="space-y-4">
                <InputText
                  class="flex-grow"
                  label="First Name"
                  :name="`parents[${index}].firstName`"
                  placeholder="First Name"
                  required
                />
                <InputText
                  label="Email Address"
                  :name="`parents[${index}].email`"
                  placeholder="Email Address"
                  required
                  type="email"
                />
                <InputPhone label="Phone" :name="`parents[${index}].phone`" />
              </div>
            </div>
            <div v-if="parentFields.length === 1" class="flex items-start">
              <BaseButton
                class="type-body-sm font-medium"
                @click="pushParent({ ...defaultParentFields })"
              >
                + Add another parent or guardian
              </BaseButton>
            </div>
          </div>
          <div v-else-if="currentStep === 2" class="space-y-7">
            <div
              v-for="(student, index) in studentFields"
              :key="student.key"
              class="space-y-7"
            >
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <h3 class="type-heading-5 font-medium">
                    Student{{ studentFields.length > 1 ? ` ${index + 1}` : '' }}
                    info
                  </h3>
                  <RemoveButton
                    v-if="index > 0"
                    @click="removeStudent(index)"
                  />
                </div>
                <p class="text-[13px]/[20px]">
                  Only your child’s first name and last name initial will be
                  shared with other families that pledge in their school year
                  group
                </p>
              </div>
              <div class="space-y-4">
                <div
                  class="flex flex-col space-y-4 md:flex-row md:space-x-5 md:space-y-0"
                >
                  <InputText
                    class="flex-grow"
                    label="First Name"
                    :name="`students[${index}].firstName`"
                    placeholder="First Name"
                    required
                  />
                  <InputText
                    class="md:w-[164px]"
                    label="Last Name Initial"
                    mask="@"
                    :name="`students[${index}].lastName`"
                    placeholder="Last Name Initial"
                    required
                  />
                </div>
                <InputPrimarySchool
                  :name="`students[${index}].primarySchool`"
                />
                <InputSelect
                  :label="`Current Year Group (${new Date().getFullYear()})`"
                  :name="`students[${index}].currentYearGroup`"
                  :options="yearGroupOptions[index]"
                  :disabled="!student.value.primarySchool"
                  :placeholder="
                    student.value.primarySchool
                      ? 'Please select'
                      : 'Select school first'
                  "
                  required
                />
                <InputRadioButtons
                  label="Take Pledge Until"
                  :name="`students[${index}].finalPledgeYear`"
                  :options="finalPledgeYearOptions"
                  required
                />
                <InputSecondarySchool
                  :name="`students[${index}].secondarySchool`"
                />
              </div>
            </div>
            <div v-if="studentFields.length < 3" class="flex items-start">
              <BaseButton
                class="type-body-sm font-medium"
                @click="pushStudent({ ...defaultStudentFields })"
              >
                + Add another student
              </BaseButton>
            </div>
          </div>
          <div v-else-if="showSuccessPage" class="space-y-8">
            <h3 class="type-heading-4 font-medium">
              You’ve joined the movement!
            </h3>
            <CohortCard
              v-for="({ studentName, stats }, index) in cohorts"
              :key="index"
              :stats="stats"
              :name="studentName"
            />
          </div>
          <div v-if="showSuccessPage">
            <NuxtLink class="btn btn-sm btn-black" to="/">
              Back to Home
            </NuxtLink>
          </div>
          <div v-else class="mt-7 flex justify-end space-x-6">
            <BaseButton
              v-if="currentStep !== 1"
              class="type-body-sm text-black/50 hover:text-black"
              @click="currentStep--"
            >
              <span>Go Back</span>
            </BaseButton>
            <BaseAsyncButton
              class="btn-sm btn-black ml-auto"
              :loading="isLoading"
              type="submit"
            >
              {{ isLastStep ? 'Sign the pledge' : 'Next' }}
            </BaseAsyncButton>
          </div>
          <div
            v-if="parentError || studentError"
            class="mt-5 flex justify-end text-right"
          >
            <span
              class="type-label text-red"
              v-text="parentError || studentError"
            />
          </div>
        </form>
      </div>
    </DefaultGrid>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import type { Parent, Student } from '#payload/types'

import {
  CreateParentDocument,
  CreateStudentDocument,
  GetCohortStatsDocument,
} from '@/graphql'

import type { CohortStats } from '@/components/cohort-card/types'

definePageMeta({
  layout: 'pledge',
})

useSeoMeta({
  title: 'Take the Pledge',
})

/**
 * Form Steps
 */
const currentStep = ref(1)
const totalSteps = ref(2)
const isLastStep = computed(() => currentStep.value === totalSteps.value)
const showSuccessPage = computed(() => currentStep.value > totalSteps.value)

const progress = computed(() =>
  showSuccessPage.value ? 1 : currentStep.value * 0.5 - 0.05,
)

/**
 * Schema
 */
const defaultParentFields = {
  firstName: '',
  email: '',
  phone: '',
}

const defaultStudentFields = {
  firstName: '',
  lastName: '',
  primarySchool: null,
  currentYearGroup: -999,
  finalPledgeYear: 'year_8',
  secondarySchool: null,
}

const initialValues = {
  parents: [{ ...defaultParentFields }],
  students: [{ ...defaultStudentFields }],
}

const parentSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  email: z.string().email(),
  phone: z.custom<string>((val) => {
    if (!val) {
      return true
    }

    if (typeof val !== 'string') {
      return false
    }

    const numbers = val
      .replace(/[^\d]/g, '')
      .split('')
      .map((d) => parseInt(d, 10))

    if (numbers[0] === 0 && numbers.length === 10) {
      return true
    } else if (numbers.length === 9) {
      return true
    }

    return false
  }, 'Invalid phone number'),
})

type ParentSchemaType = z.infer<typeof parentSchema>

const schoolSchema = z.object({
  id: z.number().min(0),
  location: z.object({
    state: z.union([
      z.literal('ACT'),
      z.literal('NSW'),
      z.literal('NT'),
      z.literal('QLD'),
      z.literal('SA'),
      z.literal('TAS'),
      z.literal('VIC'),
      z.literal('WA'),
    ]),
  }),
})

const studentSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().min(1, 'Required'),
  primarySchool: z
    .union([schoolSchema, z.null()])
    .refine((val) => val !== null, 'Required'),
  currentYearGroup: z.number().min(-1, 'Required'),
  finalPledgeYear: z.string(),
  secondarySchool: z.union([schoolSchema, z.null()]),
})

type StudentSchemaType = z.infer<typeof studentSchema>

const validationSchemas = [
  toTypedSchema(
    z.object({
      parents: parentSchema.array(),
    }),
  ),
  toTypedSchema(
    z.object({
      students: studentSchema.array(),
    }),
  ),
]

const currentSchema = computed(() => validationSchemas[currentStep.value - 1])

/**
 * Form Validation
 */
const { handleSubmit, values } = useForm<{
  parents: ParentSchemaType[]
  students: StudentSchemaType[]
}>({
  initialValues,
  keepValuesOnUnmount: true,
  validationSchema: currentSchema,
})

/**
 * Array Fields
 */
const {
  fields: parentFields,
  remove: removeParent,
  push: pushParent,
} = useFieldArray<ParentSchemaType>('parents')

const {
  fields: studentFields,
  remove: removeStudent,
  push: pushStudent,
} = useFieldArray<StudentSchemaType>('students')

/**
 * Field Options
 */
const finalPledgeYearOptions = [
  {
    label: 'Year 7',
    value: 'year_7',
  },
  {
    label: 'Year 8',
    value: 'year_8',
  },
  {
    label: 'Year 9',
    value: 'year_9',
  },
  {
    label: 'Year 10',
    value: 'year_10',
  },
]
const yearGroupOptions = computed(() => {
  return studentFields.value.map((student) => {
    const options = [
      {
        label: 'Year 1',
        value: 1,
      },
      {
        label: 'Year 2',
        value: 2,
      },
      {
        label: 'Year 3',
        value: 3,
      },
      {
        label: 'Year 4',
        value: 4,
      },
      {
        label: 'Year 5',
        value: 5,
      },
      {
        label: 'Year 6',
        value: 6,
      },
    ]

    const school = student.value.primarySchool

    if (school?.location?.state) {
      switch (school.location.state) {
        case 'NT':
          options.unshift({
            label: 'Transition',
            value: 0,
          })

          options.unshift({
            label: 'Pre-school',
            value: -1,
          })
          break
        case 'QLD':
          options.unshift({
            label: 'Prep',
            value: 0,
          })
          break
        case 'SA':
          options.unshift({
            label: 'Reception',
            value: 0,
          })
          break
        case 'VIC':
          options.unshift({
            label: 'Prep',
            value: 0,
          })
          break
        case 'WA':
          options.unshift({
            label: 'Pre-Primary',
            value: 0,
          })

          options.unshift({
            label: 'Kindergarten',
            value: -1,
          })
          break
        default:
          options.unshift({
            label: 'Kindergarten',
            value: 0,
          })
          break
      }
    }

    return options
  })
})

/**
 * Submit
 */
interface CreateParentMutation {
  createParent: Parent
}

type PartialStudent = Omit<
  Student,
  'firstName' | 'lastName' | 'finalPledgeYear' | 'secondarySchool' | 'parents'
>

interface Cohort {
  studentName: string
  stats: CohortStats
}

interface CreateStudentMutation {
  createStudent: PartialStudent
}

interface GetCohortStatsQuery {
  CohortStats: CohortStats
}

const { $sentry } = useNuxtApp()
const isLoading = ref(false)

const {
  mutate: createParent,
  error: parentError,
  onError: onCreateParentError,
} = useMutation<CreateParentMutation>(CreateParentDocument)

const {
  mutate: createStudent,
  error: studentError,
  onError: onCreateStudentError,
} = useMutation<CreateStudentMutation>(CreateStudentDocument)

const parents: Parent[] = []
const students: PartialStudent[] = []
const cohorts = ref<Cohort[]>([])

onCreateParentError((err) => {
  $sentry.captureException(err)
})

onCreateStudentError((err) => {
  $sentry.captureException(err)
})

const onSubmit = handleSubmit(async () => {
  if (isLastStep.value) {
    isLoading.value = true

    await Promise.all(
      values.parents?.map(({ firstName, email, phone }) =>
        createParent({
          data: {
            firstName: firstName.trim(),
            email,
            phone: phone
              ? `+61${phone.replace(/[^\d]/g, '')}`.replace('+610', '+61')
              : '',
          },
        }),
      ) || [],
    ).then((results) => {
      results.forEach((res) => {
        if (res?.data?.createParent) {
          parents.push(res.data.createParent)
        }
      })
    })

    for (const student of values.students) {
      const currentYear = new Date().getFullYear()
      const {
        firstName,
        lastName,
        primarySchool,
        currentYearGroup,
        finalPledgeYear,
        secondarySchool,
      } = student

      await createStudent({
        data: {
          firstName: firstName.trim(),
          lastName: lastName.trim().toUpperCase(),
          graduationYear: currentYear + 6 - currentYearGroup,
          primarySchool: primarySchool?.id,
          finalPledgeYear,
          secondarySchool: secondarySchool?.id,
          parents: parents.map(({ id }) => id),
        },
      }).then((res) => {
        if (res?.data?.createStudent) {
          students.push(res.data.createStudent)
        }
      })
    }

    await Promise.all(
      students.map(
        ({ graduationYear, primarySchool }) =>
          new Promise<GetCohortStatsQuery>((resolve) => {
            const { onResult } = useQuery<GetCohortStatsQuery>(
              GetCohortStatsDocument,
              {
                graduationYear,
                schoolID: getRelationshipNumericalID(primarySchool),
              },
            )

            onResult((result) => {
              if (result.data) {
                resolve(result.data)
              }
            })
          }),
      ),
    ).then((results) => {
      results.forEach((res, index) => {
        cohorts.value.push({
          studentName: values.students[index].firstName,
          stats: res.CohortStats,
        })
      })
    })

    isLoading.value = false

    if (!parentError.value && !studentError.value) {
      currentStep.value++
    }
  } else {
    currentStep.value++
  }
})
</script>
